import dayjs from 'dayjs';
import { EXTRA_CATEGORIES, MOCK_WORKERS, PERIODIC_CATEGORIES } from './mockData';

export const FILTER_IDS = {
  ALL: 'all',
  STAFF: 'staff',
  BOLLETTE: 'bollette',
  AFFITTO: 'affitto',
  MATERIALI: 'materiali',
  ALTRO: 'altro',
  PERIODICHE: 'periodiche',
};

export const getChairs = (apiWorkers = []) => {
  if (apiWorkers.length) {
    return apiWorkers.map((worker) => ({
      id: worker.id,
      name: worker.name,
      icon: worker.icon,
      fatturatoMese: 0,
    }));
  }
  return MOCK_WORKERS;
};

export const findChair = (poltronaId, chairs) => {
  const byId = chairs.find((chair) => String(chair.id) === String(poltronaId));
  if (byId) return byId;
  const mockIndex = MOCK_WORKERS.findIndex((worker) => worker.id === poltronaId);
  if (mockIndex >= 0 && chairs[mockIndex]) return chairs[mockIndex];
  return { id: poltronaId, name: '—', fatturatoMese: 0 };
};

export const getStaffBonus = (expense, chairs) => {
  if (expense.tipo !== 'staff' || !(expense.bonusPct > 0)) return 0;
  if (expense.fatturatoMese != null) {
    return Number(expense.fatturatoMese) * (expense.bonusPct / 100);
  }
  const chair = findChair(expense.poltronaId, chairs);
  return (chair.fatturatoMese || 0) * (expense.bonusPct / 100);
};

export const getExpenseAmount = (expense, chairs) => {
  if (expense.tipo === 'staff') {
    if (expense.valoreBonus != null) {
      return (expense.importoBase || 0) + Number(expense.valoreBonus);
    }
    return (expense.importoBase || 0) + getStaffBonus(expense, chairs);
  }
  return expense.importo || 0;
};

export const isInMonth = (date, period) => dayjs(date).isSame(period, 'month');

export const filterByMonth = (expenses, period) =>
  expenses.filter((expense) => isInMonth(expense.data, period));

export const sumExpenses = (expenses, chairs) =>
  expenses.reduce((total, expense) => total + getExpenseAmount(expense, chairs), 0);

export const sumDue = (expenses, chairs) =>
  expenses
    .filter((expense) => expense.stato === 'da_pagare')
    .reduce((total, expense) => total + getExpenseAmount(expense, chairs), 0);

export const sumPaid = (expenses, chairs) =>
  expenses
    .filter((expense) => expense.stato === 'pagato')
    .reduce((total, expense) => total + getExpenseAmount(expense, chairs), 0);

export const sumStaff = (expenses, chairs) =>
  sumExpenses(
    expenses.filter((expense) => expense.tipo === 'staff'),
    chairs,
  );

export const isPeriodicExpense = (expense) =>
  Boolean(expense.periodica) || PERIODIC_CATEGORIES.includes(expense.categoria);

export const filterByPill = (expenses, filterId) => {
  if (!filterId || filterId === FILTER_IDS.ALL) return expenses;
  if (filterId === FILTER_IDS.STAFF) return expenses.filter((expense) => expense.tipo === 'staff');
  if (filterId === FILTER_IDS.PERIODICHE) return expenses.filter(isPeriodicExpense);
  if (filterId === FILTER_IDS.ALTRO) {
    return expenses.filter(
      (expense) => expense.tipo === 'operativa' && EXTRA_CATEGORIES.includes(expense.categoria),
    );
  }
  return expenses.filter(
    (expense) => expense.tipo === 'operativa' && expense.categoria === filterId,
  );
};

export const formatRecurringBadge = (expense, language = 'it') => {
  if (!expense.periodica && !expense.recurrenceId) return null;
  const n = expense.periodicaOgni || 1;
  let suffix;
  if (language === 'fr') suffix = n === 1 ? '1 mois' : `${n} mois`;
  else if (language === 'en') suffix = n === 1 ? '1 mo' : `${n} mo`;
  else suffix = n === 1 ? '1 mese' : `${n} mesi`;
  const label = language === 'fr' ? 'Récurrente' : language === 'en' ? 'Recurring' : 'Ricorrente';
  return `${label} · ${suffix}`;
};

export const formatEuro = (value, language = 'it') =>
  new Intl.NumberFormat(language, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export const formatMonthLabel = (period, language = 'it') => {
  const label = dayjs(period).locale(language).format('MMMM YYYY');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const monthBarLabel = (month, language) =>
  month.locale(language).format('MMM').replace('.', '').replace(/^./, (char) => char.toUpperCase());

export const getMonthSeriesFromTrend = (trend, period, language = 'it', count = 4) => {
  const trendMap = Object.fromEntries(
    (trend || []).map((row) => [row.month, Number(row.totale) || 0]),
  );
  const selected = period.startOf('month');
  const now = dayjs().startOf('month');
  const offset = selected.diff(now, 'month');

  let windowStart;
  if (offset >= -(count - 2) && offset <= 0) {
    windowStart = now.subtract(count - 1, 'month');
  } else if (offset < -(count - 2)) {
    windowStart = selected.subtract(1, 'month');
  } else {
    windowStart = selected.subtract(count - 2, 'month');
  }

  const months = Array.from({ length: count }, (_, index) => {
    const month = windowStart.add(index, 'month').startOf('month');
    const key = month.format('YYYY-MM');
    return {
      key,
      month,
      label: monthBarLabel(month, language),
      total: trendMap[key] || 0,
      isCurrent: month.isSame(selected, 'month'),
    };
  });

  const peak = Math.max(...months.map((item) => item.total), 0);
  return months.map((item) => ({
    ...item,
    isHighest: peak > 0 && item.total === peak,
  }));
};

export const getTrendTotalForMonth = (trend, period) => {
  const key = period.format('YYYY-MM');
  const row = (trend || []).find((item) => item.month === key);
  return row ? Number(row.totale) || 0 : null;
};

export const getMonthSeries = (expenses, chairs, period, language = 'it', count = 4) => {
  const selected = period.startOf('month');
  const now = dayjs().startOf('month');
  const offset = selected.diff(now, 'month');

  let windowStart;
  if (offset >= -(count - 2) && offset <= 0) {
    windowStart = now.subtract(count - 1, 'month');
  } else if (offset < -(count - 2)) {
    windowStart = selected.subtract(1, 'month');
  } else {
    windowStart = selected.subtract(count - 2, 'month');
  }

  const months = Array.from({ length: count }, (_, index) => {
    const month = windowStart.add(index, 'month').startOf('month');
    return {
      key: month.format('YYYY-MM'),
      month,
      label: monthBarLabel(month, language),
      total: sumExpenses(filterByMonth(expenses, month), chairs),
      isCurrent: month.isSame(selected, 'month'),
    };
  });

  const selectedTotal = months.find((item) => item.isCurrent)?.total || 0;
  const filled = months.map((item, index) => {
    if (item.total > 0) return item;
    const fictional = Math.max(180, Math.round(selectedTotal * (0.58 + index * 0.1)));
    return { ...item, total: fictional };
  });

  const peak = Math.max(...filled.map((item) => item.total), 0);
  return filled.map((item) => ({
    ...item,
    isHighest: item.total === peak,
  }));
};
